/**
 * ProtectedRoute.tsx
 * 
 * This component provides route protection for authenticated pages.
 * It handles:
 * 
 * - Authentication state checking
 * - Redirecting unauthenticated users to login
 * - Loading states while checking authentication
 * - Error handling for authentication issues
 * - Conditional rendering of protected content
 * 
 * The component wraps any route that requires authentication
 * and ensures only logged-in users can access protected pages.
 */

import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { state } = useAuth()

  // Show loading state while checking authentication
  if (state.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!state.user || !state.token) {
    return <Navigate to="/login" replace />
  }

  // Render protected content if authenticated
  return <>{children}</>
}

export default ProtectedRoute 